import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, X, Send, Loader2, Sparkles, ArrowRight, Trash2, Eye,
} from "lucide-react";
import { api } from "../../lib/api";

const SESSION_KEY = "argus.goddy.session-v1";
const HIDDEN_KEY = "argus.goddy.hidden-v1";
const HISTORY_LIMIT = 50;

function getOrCreateSession() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `g_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const STARTER_PROMPTS = [
  "What does ArgusSecure do?",
  "How much does it cost?",
  "How is the AI Fix-It Agent different?",
  "Who built this?",
];

const GREETING = {
  role: "assistant",
  content:
    "Hi — I'm Goddy, the AI agent for ArgusSecure. Ask me anything about the platform, pricing, or how to get started. I'll route you to the right page when it helps.",
  suggestion: { label: null, path: null },
};

export default function GoddyAgent() {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDDEN_KEY) === "1");
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [bumped, setBumped] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getOrCreateSession());

  const popOut = () => {
    localStorage.setItem(HIDDEN_KEY, "1");
    setHidden(true);
    setBumped(false);
  };
  const popIn = () => {
    localStorage.removeItem(HIDDEN_KEY);
    setHidden(false);
  };

  // Lazy-load history when the panel first opens
  useEffect(() => {
    if (!open || hydrated) return;
    setHydrated(true);
    api
      .get(`/goddy/history/${sessionId.current}`)
      .then((r) => {
        const hist = (r.data?.messages || []).slice(-HISTORY_LIMIT);
        if (hist.length > 0) {
          setMessages([GREETING, ...hist]);
        }
      })
      .catch(() => {});
  }, [open, hydrated]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // One-time "say hi" bump for first-time visitors — auto-dismisses after 8s
  useEffect(() => {
    if (hidden) return;
    const seen = localStorage.getItem("argus.goddy.seen-v1");
    if (seen) return;
    const t = setTimeout(() => setBumped(true), 4000);
    return () => clearTimeout(t);
  }, [hidden]);

  // Auto-hide the say-hi bump if the user doesn't interact within 8s
  useEffect(() => {
    if (!bumped) return;
    const t = setTimeout(() => dismissBump(), 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bumped]);

  const dismissBump = () => {
    localStorage.setItem("argus.goddy.seen-v1", "1");
    setBumped(false);
  };

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setInput("");
    setFollowups([]);
    setMessages((m) => [...m, { role: "user", content: t }]);
    setSending(true);
    dismissBump();
    try {
      const r = await api.post("/goddy/chat", {
        session_id: sessionId.current,
        message: t,
        page_path: window.location.pathname,
      });
      const d = r.data || {};
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: d.reply || "(no reply)",
          suggestion: d.suggestion || { label: null, path: null },
        },
      ]);
      setFollowups(d.followup_questions || []);
    } catch (e) {
      // Prefer the backend's own error message if present — it's much more
      // helpful (e.g. "Goddy isn't configured — admin: set EMERGENT_LLM_KEY").
      const serverReply = e?.response?.data?.reply;
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            serverReply ||
            (e?.response?.status === 429
              ? "You're moving fast — give me a minute to catch up."
              : "Sorry, I hit a wall. Try again, or tap the gold 'Talk to Security' button for a human."),
          suggestion: e?.response?.data?.suggestion || { label: null, path: null },
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    if (!confirm("Clear this conversation? A fresh session ID will be generated.")) return;
    localStorage.removeItem(SESSION_KEY);
    sessionId.current = getOrCreateSession();
    setMessages([GREETING]);
    setFollowups([]);
    setHydrated(true);
  };

  return (
    <>
      {/* Edge-tab — visible only when launcher has been dismissed. Click to pop back in. */}
      {hidden && !open && (
        <button
          onClick={popIn}
          aria-label="Show Goddy"
          title="Show Goddy"
          className="fixed z-[60] right-0 bottom-24 h-12 w-2.5 rounded-l-full bg-gold/40 hover:bg-gold hover:w-3 transition-all"
          data-testid="goddy-summoner"
        />
      )}

      {/* Floating launcher button — pops in/out with a smooth slide. */}
      <div
        className={`fixed z-[60] bottom-5 right-5 flex items-end gap-1 transition-all duration-300 ${
          open || hidden ? "opacity-0 translate-x-20 pointer-events-none" : "opacity-100 translate-x-0"
        }`}
      >
        <button
          onClick={() => { setOpen(true); dismissBump(); }}
          aria-label="Open Goddy's AI Agent"
          className="w-14 h-14 rounded-full bg-gold text-paper shadow-lg hover:shadow-xl hover:bg-gold2 flex items-center justify-center"
          data-testid="goddy-launcher"
        >
          <Sparkles size={22} />
          <span className="sr-only">Open chat</span>
        </button>
        <button
          onClick={popOut}
          aria-label="Hide Goddy"
          title="Hide Goddy"
          className="w-5 h-5 -ml-2 mb-1 rounded-full bg-panel border border-line text-ink/70 hover:text-ink hover:bg-raised flex items-center justify-center shadow"
          data-testid="goddy-dismiss"
        >
          <X size={10} />
        </button>
      </div>

      {/* "say hi" speech bubble */}
      {bumped && !open && !hidden && (
        <button
          onClick={() => { setOpen(true); dismissBump(); }}
          className="fixed z-[60] bottom-24 right-5 max-w-[260px] rounded-2xl rounded-br-sm border border-gold/40 bg-panel text-ink shadow-lg p-4 text-sm text-left hover:bg-raised transition-opacity animate-in fade-in"
          data-testid="goddy-bump"
        >
          <span className="block text-[10px] uppercase tracking-[0.2em] text-gold mb-1">Goddy's AI Agent</span>
          Hi — ask me anything about ArgusSecure. I'll route you to the right page.
        </button>
      )}

      {/* Chat panel */}
      <div
        className={`fixed z-[60] bottom-5 right-5 w-[min(420px,calc(100vw-2rem))] h-[min(640px,calc(100vh-2.5rem))] bg-paper border border-line rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${
          open && !hidden ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        data-testid="goddy-panel"
        aria-hidden={!open || hidden}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-line bg-panel flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg leading-tight">Goddy's AI Agent</h3>
            <p className="text-[11px] text-ink/50 leading-tight">ArgusSecure · Claude Sonnet 4.5</p>
          </div>
          <button
            onClick={reset}
            className="p-1.5 text-ink/40 hover:text-sev-critical"
            aria-label="Clear conversation"
            title="Clear conversation"
            data-testid="goddy-reset"
          >
            <Trash2 size={14} />
          </button>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 text-ink/60 hover:text-ink"
            aria-label="Close chat"
            data-testid="goddy-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-paper" data-testid="goddy-messages">
          {messages.map((m, i) => (
            <div key={m.id || `${m.role}-${m.at || i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words ${
                  m.role === "user"
                    ? "bg-gold/15 text-ink rounded-br-sm border border-gold/30"
                    : "bg-panel text-ink rounded-bl-sm border border-line"
                }`}
              >
                {m.content}
                {m.role === "assistant" && m.suggestion?.path && (
                  <Link
                    to={m.suggestion.path}
                    onClick={() => setOpen(false)}
                    className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-gold hover:text-gold2 font-semibold underline-offset-2 hover:underline"
                    data-testid={`goddy-cta-${i}`}
                  >
                    <ArrowRight size={12} />
                    {m.suggestion.label || "Take me there"}
                  </Link>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-panel border border-line rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-ink/60 inline-flex items-center gap-2" data-testid="goddy-typing">
                <Loader2 size={14} className="animate-spin text-gold" /> Goddy is thinking…
              </div>
            </div>
          )}
        </div>

        {/* Starter prompts (only on a fresh greeting) */}
        {messages.length === 1 && !sending && (
          <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="goddy-starters">
            {STARTER_PROMPTS.map((p) => (
              <button
                key={p}
                onClick={() => send(p)}
                className="text-[12px] px-2.5 py-1.5 rounded-full border border-line bg-panel hover:bg-raised text-ink/80 hover:text-ink"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {/* Follow-up suggestions */}
        {followups.length > 0 && !sending && (
          <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="goddy-followups">
            {followups.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="text-[12px] px-2.5 py-1.5 rounded-full border border-gold/30 bg-gold/5 hover:bg-gold/10 text-ink/85"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="px-3 py-3 border-t border-line bg-panel flex gap-2"
          data-testid="goddy-composer"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything about ArgusSecure…"
            className="flex-1 h-10 px-3 rounded-md bg-paper border border-line text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-gold/50"
            data-testid="goddy-input"
            maxLength={2000}
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="h-10 w-10 rounded-md bg-gold text-paper hover:bg-gold2 disabled:opacity-50 flex items-center justify-center"
            data-testid="goddy-send"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </form>
        <p className="text-[10px] text-ink/40 text-center pb-2 px-3">
          Powered by Claude Sonnet 4.5 · Built by GSGD FIELD SOLUTIONS ENGINEERING
        </p>
      </div>
    </>
  );
}
